import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Calendar, ShieldCheck, Lock, EyeOff, Award, Heart, ExternalLink, UserPlus, UserCheck, Clock, Bookmark, ThumbsUp } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import GlassBoxBadge from '@/components/GlassBoxBadge';

const mockProfiles = {
  'sarah-chen': {
    name: 'Sarah Chen',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=0',
    initials: 'SC',
    memberSince: 'May 2024',
    location: 'San Francisco, CA',
    privacy: 'Public',
    headline: 'Impact Investor | Monthly Donor | Building a transparent world',
    totalDonated: 32334,
    projectsSupported: 11,
    causesSupported: 4,
    onChainReceipts: 28,
    followers: 248,
    following: 112,
    badges: [
      { name: 'Monthly Giver', color: 'bg-purple-50 text-purple-600 border-purple-200' },
      { name: 'Verified Donor', color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
      { name: 'Top Contributor', color: 'bg-blue-50 text-blue-600 border-blue-200' },
      { name: 'Founding Member', color: 'bg-amber-50 text-amber-600 border-amber-200' },
    ],
    endorsements: [
      { skill: 'Education', count: 18, endorsers: ['Michael R.', 'Jessica O.', 'David P.'] },
      { skill: 'Clean Water', count: 14, endorsers: ['Emily W.', 'James W.'] },
      { skill: 'Healthcare', count: 9, endorsers: ['Alex R.'] },
      { skill: 'Youth Development', count: 7, endorsers: ['Maria S.'] },
      { skill: 'Disaster Relief', count: 5, endorsers: ['David P.'] },
    ],
    givingHistory: [
      { project: '3,000 Meals for Tampa Bay', amount: 500, date: 'Feb 12, 2026', txHash: '0x920581d82c9c3a08fc5cc38ecae2fcef37d73135da4f4d934d2e2e9f86fb95f7' },
      { project: 'Clean Water for Kigali Schools', amount: 1200, date: 'Jan 28, 2026', txHash: '0x6a8f6058339af309ef6893d89a133123255059c30d7b1fa9a67864e18a14403d' },
      { project: 'Reforestation in Appalachia', amount: 750, date: 'Jan 5, 2026', txHash: '0x01939bb7eca612fd1047e3959fee313f07795aa6c291f171f796123d6eb6e60c' },
      { project: 'Youth Code Academy', amount: 300, date: 'Dec 19, 2025', txHash: '0x2c7edf238a3503ee0e7ee1a9548b39c87e8fd185518dd48baa457ea5c78bde7c' },
      { project: 'Solar-Powered Clinics', amount: 2000, date: 'Nov 30, 2025', txHash: '0xfea744d48ad4a59f2758a4975bc8ba819123823c27870e507c18e61ab47a66e7' },
    ],
  },
  'james-wright': {
    name: 'James Wright',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=10',
    initials: 'JW',
    memberSince: 'Sep 2024',
    location: 'Austin, TX',
    privacy: 'Private',
    headline: 'Quietly making a difference',
    totalDonated: 15800,
    projectsSupported: 6,
    causesSupported: 2,
    onChainReceipts: 12,
    followers: 89,
    following: 34,
    badges: [
      { name: 'Verified Donor', color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
      { name: 'Monthly Giver', color: 'bg-purple-50 text-purple-600 border-purple-200' },
    ],
    endorsements: [
      { skill: 'Education', count: 5, endorsers: ['Sarah C.'] },
      { skill: 'Healthcare', count: 3, endorsers: ['Alex R.'] },
    ],
    givingHistory: [],
  },
  'anonymous-donor': {
    name: 'Anonymous',
    avatar: null,
    initials: '?',
    memberSince: 'Jan 2025',
    location: null,
    privacy: 'Anonymous',
    headline: null,
    totalDonated: null,
    projectsSupported: null,
    causesSupported: null,
    onChainReceipts: null,
    followers: 0,
    following: 0,
    badges: [
      { name: 'Verified Donor', color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
    ],
    endorsements: [],
    givingHistory: [],
  },
};

const mockSavedPosts = [
  { id: 's1', type: 'project', title: '3,000 Meals for Tampa Bay', org: 'Feed Tampa Bay', progress: 78, image: 'https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=400' },
  { id: 's2', type: 'story', title: 'How clean water changed a village', org: 'Rwanda Rising', progress: null, image: 'https://images.unsplash.com/photo-1541544537156-7627a7a4aa1c?w=400' },
  { id: 's3', type: 'project', title: 'Youth Code Academy - Medellín', org: 'Code for Change', progress: 52, image: 'https://images.unsplash.com/photo-1509062522246-3755977927d7?w=400' },
  { id: 's4', type: 'project', title: 'Solar-Powered Clinics', org: 'Health Beyond Borders', progress: 91, image: 'https://images.unsplash.com/photo-1532601224476-15c79f2f7a51?w=400' },
];

const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5 } }) };

function PrivacyBadge({ level }) {
  const config = {
    Public: { icon: ShieldCheck, bg: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
    Private: { icon: Lock, bg: 'bg-amber-50 text-amber-600 border-amber-200' },
    Anonymous: { icon: EyeOff, bg: 'bg-gray-100 text-gray-500 border-gray-200' },
  }[level] || { icon: ShieldCheck, bg: 'bg-gray-100 text-gray-500 border-gray-200' };
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-medium ${config.bg}`} data-testid="privacy-badge">
      <Icon className="w-3.5 h-3.5" />{level}
    </span>
  );
}

function ConnectButton() {
  const [state, setState] = useState('idle'); // idle | pending | connected

  const handleClick = () => {
    if (state === 'idle') {
      setState('pending');
      toast.success('Connection request sent!');
    } else if (state === 'pending') {
      setState('idle');
      toast('Connection request withdrawn');
    } else {
      setState('idle');
      toast('Disconnected');
    }
  };

  const config = {
    idle: { label: 'Connect', icon: UserPlus, className: 'bg-gradient-to-r from-[#7C3AED] to-[#2563EB] text-white border-0 hover:shadow-lg' },
    pending: { label: 'Pending', icon: Clock, className: 'border-amber-400 text-amber-600 bg-amber-50' },
    connected: { label: 'Connected', icon: UserCheck, className: 'border-[#10B981] text-[#10B981] bg-[#10B981]/5' },
  }[state];
  const Icon = config.icon;

  return (
    <Button
      onClick={handleClick}
      variant={state === 'idle' ? 'default' : 'outline'}
      className={`rounded-full h-10 gap-2 text-sm font-medium transition-all ${config.className}`}
      data-testid="connect-btn"
    >
      <Icon className="w-4 h-4" /> {config.label}
    </Button>
  );
}

function EndorsementSection({ endorsements }) {
  const [endorsed, setEndorsed] = useState({});

  const handleEndorse = (skill) => {
    setEndorsed(prev => {
      const next = { ...prev, [skill]: !prev[skill] };
      toast.success(next[skill] ? `You endorsed ${skill}!` : `Removed endorsement for ${skill}`);
      return next;
    });
  };

  if (!endorsements?.length) return null;
  return (
    <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] p-6" data-testid="endorsement-section">
      <h2 className="font-heading font-semibold text-[#111827] mb-4">Cause Endorsements</h2>
      <div className="space-y-3">
        {endorsements.map((e, i) => (
          <div key={i} className="flex items-center gap-3 py-2" data-testid={`endorsement-${i}`}>
            <button
              onClick={() => handleEndorse(e.skill)}
              className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all ${endorsed[e.skill] ? 'bg-[#7C3AED] text-white' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
              data-testid={`endorse-btn-${i}`}
            >
              <ThumbsUp className={`w-4 h-4 ${endorsed[e.skill] ? 'fill-current' : ''}`} />
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-heading font-semibold text-sm text-[#111827]">{e.skill}</span>
                <span className="text-xs text-[#7C3AED] font-medium font-body">{e.count + (endorsed[e.skill] ? 1 : 0)} endorsements</span>
              </div>
              <p className="text-xs text-gray-400 font-body truncate">Endorsed by {e.endorsers.join(', ')}{endorsed[e.skill] ? ', You' : ''}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SavedTab() {
  return (
    <div className="space-y-4" data-testid="saved-tab">
      {mockSavedPosts.length === 0 ? (
        <div className="text-center py-12">
          <Bookmark className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500 font-body">No saved posts yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {mockSavedPosts.map(post => (
            <div key={post.id} className="bg-white rounded-xl overflow-hidden shadow-[0_4px_15px_rgb(0,0,0,0.04)] border border-gray-100 hover:shadow-md transition-shadow" data-testid={`saved-post-${post.id}`}>
              <img src={post.image} alt={post.title} className="w-full h-32 object-cover" />
              <div className="p-4">
                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${post.type === 'project' ? 'bg-[#7C3AED]/10 text-[#7C3AED]' : 'bg-[#2563EB]/10 text-[#2563EB]'}`}>
                  {post.type === 'project' ? 'Project' : 'Story'}
                </span>
                <h3 className="font-heading font-semibold text-sm text-[#111827] mt-2 truncate">{post.title}</h3>
                <p className="text-xs text-gray-500 font-body mt-0.5">{post.org}</p>
                {post.progress !== null && (
                  <div className="mt-2">
                    <div className="w-full h-1.5 rounded-full bg-gray-100 overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-[#7C3AED] to-[#2563EB]" style={{ width: `${post.progress}%` }} />
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1">{post.progress}% funded</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DonorProfilePage() {
  const { username } = useParams();
  const profile = mockProfiles[username];

  if (!profile) return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center" data-testid="profile-not-found">
      <div className="text-center">
        <h2 className="text-2xl font-heading font-semibold text-[#111827] mb-2">Profile Not Found</h2>
        <p className="text-gray-500 font-body">This user profile doesn't exist.</p>
      </div>
    </div>
  );

  const isAnonymous = profile.privacy === 'Anonymous';
  const isPrivate = profile.privacy === 'Private';

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="donor-profile-page">
      {/* Cover Banner */}
      <div className="relative h-32 sm:h-44 md:h-56 bg-gradient-to-r from-[#7C3AED] via-[#6D28D9] to-[#2563EB]" data-testid="profile-cover">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(255,255,255,0.2) 0%, transparent 50%)' }} />
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
      </div>

      <div className="max-w-3xl mx-auto px-4 -mt-16 relative z-10">
        {/* Profile Header Card */}
        <motion.div custom={0} variants={fadeUp} initial="hidden" animate="visible" className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] p-6 sm:p-8 mb-6">
          <div className="flex flex-col sm:flex-row items-start gap-5">
            <div className="w-24 h-24 rounded-2xl bg-gradient-to-r from-[#7C3AED] to-[#2563EB] flex items-center justify-center text-white font-heading font-semibold text-3xl shrink-0 border-4 border-white shadow-lg -mt-14 sm:-mt-14" data-testid="profile-avatar">
              {profile.avatar && !isAnonymous ? <img src={profile.avatar} alt={profile.name} className="w-24 h-24 rounded-2xl" /> : profile.initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="flex items-center gap-3 flex-wrap mb-1">
                    <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-[#111827]" data-testid="profile-name">{profile.name}</h1>
                    <PrivacyBadge level={profile.privacy} />
                  </div>
                  {profile.headline && (
                    <p className="text-sm text-[#4B5563] font-body mb-2" data-testid="profile-headline">{profile.headline}</p>
                  )}
                  <div className="flex items-center gap-4 text-sm text-gray-500 font-body flex-wrap">
                    {profile.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{profile.location}</span>}
                    <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />Member since {profile.memberSince}</span>
                  </div>
                </div>
                {!isAnonymous && <ConnectButton />}
              </div>
              {!isAnonymous && (
                <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100">
                  <span className="text-sm font-body"><strong className="font-heading text-[#111827]">{(profile.followers || 0).toLocaleString()}</strong> <span className="text-gray-500">followers</span></span>
                  <span className="text-sm font-body"><strong className="font-heading text-[#111827]">{(profile.following || 0).toLocaleString()}</strong> <span className="text-gray-500">following</span></span>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {isAnonymous ? (
          <motion.div custom={1} variants={fadeUp} initial="hidden" animate="visible" className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] p-8 text-center">
            <EyeOff className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h2 className="text-lg font-heading font-semibold text-[#111827] mb-2">This Donor is Anonymous</h2>
            <p className="text-sm text-gray-500 font-body max-w-md mx-auto">This donor has chosen to keep their impact private. Their contributions are still verified on-chain.</p>
            <div className="mt-4"><GlassBoxBadge label="On-Chain Verified" /></div>
          </motion.div>
        ) : (
          <>
            {/* Impact Summary */}
            <motion.div custom={1} variants={fadeUp} initial="hidden" animate="visible" className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              {[
                { label: 'Total Donated', value: profile.totalDonated ? `$${profile.totalDonated.toLocaleString()}` : 'Hidden', icon: Heart, accent: 'bg-[#7C3AED]' },
                { label: 'Projects', value: profile.projectsSupported ?? 'Hidden', icon: Award, accent: 'bg-[#2563EB]' },
                { label: 'Causes', value: profile.causesSupported ?? 'Hidden', icon: ShieldCheck, accent: 'bg-[#EC4899]' },
                { label: 'Receipts', value: profile.onChainReceipts ?? 'Hidden', icon: ExternalLink, accent: 'bg-[#10B981]' },
              ].map((s, i) => (
                <div key={i} className="bg-white rounded-2xl shadow-[0_4px_20px_rgb(0,0,0,0.04)] p-4 flex items-start gap-3" data-testid={`impact-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${s.accent}`}>
                    <s.icon className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-lg font-heading font-semibold text-[#111827]">{s.value}</p>
                    <p className="text-xs text-gray-500 font-body">{s.label}</p>
                  </div>
                  {i === 0 && <div className="ml-auto"><GlassBoxBadge label="Verified" /></div>}
                </div>
              ))}
            </motion.div>

            {/* Tabs: Overview / Saved / Endorsements */}
            <motion.div custom={2} variants={fadeUp} initial="hidden" animate="visible">
              <Tabs defaultValue="overview" className="w-full">
                <TabsList className="w-full justify-start bg-white rounded-xl p-1 shadow-[0_4px_15px_rgb(0,0,0,0.03)] mb-6 h-auto overflow-x-auto scrollbar-hide" data-testid="profile-tabs">
                  {['Overview', 'Saved', 'Endorsements'].map(tab => (
                    <TabsTrigger key={tab} value={tab.toLowerCase()} className="rounded-lg px-5 py-2 text-sm font-body data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#7C3AED] data-[state=active]:to-[#2563EB] data-[state=active]:text-white" data-testid={`profile-tab-${tab.toLowerCase()}`}>
                      {tab}
                    </TabsTrigger>
                  ))}
                </TabsList>

                <TabsContent value="overview">
                  <div className="space-y-6">
                    {/* Badges */}
                    <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] p-6">
                      <h2 className="font-heading font-semibold text-[#111827] mb-4">Badges</h2>
                      <div className="flex flex-wrap gap-2">
                        {profile.badges.map((b, i) => (
                          <span key={i} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-medium ${b.color}`} data-testid={`badge-${i}`}>
                            <Award className="w-3.5 h-3.5" />{b.name}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Giving History */}
                    {!isPrivate && profile.givingHistory.length > 0 && (
                      <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] p-6">
                        <h2 className="font-heading font-semibold text-[#111827] mb-4">Giving History</h2>
                        <div className="space-y-0">
                          {profile.givingHistory.map((tx, i) => (
                            <div key={i} className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0" data-testid={`tx-${i}`}>
                              <div className="flex-1 min-w-0">
                                <p className="font-heading font-medium text-sm text-[#111827] truncate">{tx.project}</p>
                                <p className="text-xs text-gray-400 font-body">{tx.date}</p>
                              </div>
                              <div className="text-right ml-4 shrink-0">
                                <p className="font-heading font-semibold text-sm text-[#111827]">${tx.amount.toLocaleString()}</p>
                                <a href={`https://bscscan.com/tx/${tx.txHash}`} target="_blank" rel="noopener noreferrer" className="text-xs text-[#7C3AED] hover:text-[#2563EB] font-mono transition-colors" data-testid={`tx-hash-${i}`}>
                                  {tx.txHash.slice(0, 8)}...{tx.txHash.slice(-4)}
                                </a>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {isPrivate && (
                      <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] p-6 text-center">
                        <Lock className="w-8 h-8 text-amber-400 mx-auto mb-3" />
                        <p className="text-sm text-gray-500 font-body">Giving history is private for this donor.</p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="saved">
                  <SavedTab />
                </TabsContent>

                <TabsContent value="endorsements">
                  <EndorsementSection endorsements={profile.endorsements} />
                </TabsContent>
              </Tabs>
            </motion.div>
          </>
        )}
      </div>
    </div>
  );
}
